import type {CharacterJournal} from "../types/character-journal.types";
import type {
  AdviceCommand,
  AdviceReceipt,
  CharacterSnapshot,
  DecisionReceipt,
} from '../types/character.types';
export interface CharacterState {
journal: CharacterJournal | null; journalOpened:boolean; journalLoading:boolean; journalFailed:boolean;
  heroId: number | null;
  snapshot: CharacterSnapshot | null;
  pending: AdviceCommand | null;
  sending: boolean;
  advice: AdviceReceipt | null;
  decision: DecisionReceipt | null;
  lastDecisionRevision: number;
  error: string | null;
  unavailable: boolean;
}
export type CharacterEvent =
| {type:"journal_begin"} | {type:"journal_error"} | {type:"journal";value:CharacterJournal}
  | { type: 'snapshot'; value: CharacterSnapshot }
  | { type: 'advice'; value: AdviceReceipt }
  | { type: 'decision'; value: DecisionReceipt }
  | { type: 'begin'; value: AdviceCommand }
  | { type: 'retry' }
  | { type: 'error'; value: string }
  | { type: 'snapshot_error'; value: string }
  | { type: 'unavailable' }
  | { type: 'dismiss' }
  | { type: 'terminal'; value: string };
export function initialCharacterState(heroId: number | null): CharacterState {
  return {
    heroId, journal:null,journalOpened:false,journalLoading:false,journalFailed:false,
    snapshot: null,
    pending: null,
    sending: false,
    advice: null,
    decision: null,
    lastDecisionRevision: 0,
    error: null,
    unavailable: false,
  };
}
export function characterReducer(
  state: CharacterState,
  event: CharacterEvent,
): CharacterState {
  if(event.type === "journal_begin") return {...state,journalOpened:true,journalLoading:true,journalFailed:false};
if(event.type === "journal_error") return {...state,journalLoading:false,journalFailed:true};
if(event.type === "journal") {
 const v=event.value;
 if(v.heroId!==state.heroId||v.narrativeRevision<(state.snapshot?.revision??0)||v.narrativeRevision<(state.journal?.narrativeRevision??0))return {...state,journalLoading:false};
 return {...state,journal:structuredClone(v),journalLoading:false,journalFailed:false};
}
if (event.type === 'dismiss') return { ...state, decision: null };
  if (event.type === 'terminal')
    return { ...state, error: event.value, pending: null, sending: false };
  if (event.type === 'snapshot') {
    const incoming = event.value;
    if (incoming.heroId !== state.heroId) return state;
    if (
      state.snapshot &&
      (incoming.revision < state.snapshot.revision ||
        (incoming.revision === state.snapshot.revision &&
          Date.parse(incoming.serverRealUTCAt) <=
            Date.parse(state.snapshot.serverRealUTCAt)))
    )
      return state;
    const previousActive = state.snapshot?.active;
    if (
      previousActive &&
      incoming.active &&
      previousActive.id === incoming.active.id &&
      incoming.active.revision < previousActive.revision
    )
      return state;
    const changed = state.snapshot?.active?.id !== incoming.active?.id;
    const accepted = incoming.active?.advice;
    const advancedPendingStage =
      state.pending &&
      incoming.active &&
      state.pending.stageRevision < incoming.active.revision &&
      accepted?.command.commandId !== state.pending.commandId;

    const recoveredAdvice =
      accepted &&
      (!state.pending || accepted.command.commandId === state.pending.commandId)
        ? { advice: structuredClone(accepted), sending: false, error: null }
        : {};

    return {
      ...state,
      snapshot: structuredClone(incoming),
      unavailable: false,
      error: state.pending && !incoming.active?.advice ? state.error : null,
      ...recoveredAdvice,
      ...(incoming.active?.status !== 'active' || advancedPendingStage
        ? { pending: null, sending: false, error: null }
        : {}),
      ...(changed
        ? {
            decision: null,
            lastDecisionRevision: 0,
            advice: null,
            pending: null,
            sending: false,
          }
        : {}),
    };
  }
  if (event.type === 'begin') {
    if (
      state.pending ||
      !state.snapshot?.active ||
      event.value.instanceId !== state.snapshot.active.id ||
      event.value.stageRevision !== state.snapshot.active.revision
    )
      return state;
    return {
      ...state,
      pending: structuredClone(event.value),
      sending: true,
      error: null,
      decision: null,
    };
  }
  if (event.type === 'retry')
    return state.pending ? { ...state, sending: true, error: null } : state;
  if (event.type === 'unavailable')
    return { ...state, unavailable: true, sending: false, error: null };
  if (event.type === 'snapshot_error')
    return { ...state, error: event.value };
  if (event.type === 'error')
    return { ...state, sending: false, error: event.value };
  if (event.value.heroId !== state.heroId) return state;
  if (event.type === 'advice') {
    const value = event.value;
    const active = state.snapshot?.active;
    if (!active || value.command.stageRevision < active.revision - 1)
      return state;
    if (
      value.command.instanceId !== state.snapshot?.active?.id ||
      (state.pending && value.command.commandId !== state.pending.commandId)
    )
      return state;
    if (state.advice && value.ownedSequence <= state.advice.ownedSequence)
      return state;
    return {
      ...state,
      advice: structuredClone(value),
      sending: false,
      error: null,
    };
  }
  const value = event.value;
  if (
    !state.snapshot?.active ||
    value.instanceId !== state.snapshot.active.id ||
    value.fromRevision < state.snapshot.active.revision - 1 ||
    value.toRevision <= state.lastDecisionRevision
  )
    return state;
  if (
    state.pending &&
    value.adviceCommandId &&
    value.adviceCommandId !== state.pending.commandId
  )
    return state;
  const resolvesPending =
    !state.pending ||
    value.adviceCommandId === state.pending.commandId ||
    (!value.adviceCommandId &&
      value.fromRevision === state.pending.stageRevision);
  return {
    ...state,
    decision: structuredClone(value),
    lastDecisionRevision: value.toRevision,
    ...(resolvesPending ? { pending: null, sending: false, error: null } : {}),
  };
}
